import { call, put, all, takeEvery, select } from 'redux-saga/effects'

import { getInitialPartnershipId } from '../../helpers/handlePartnershipSelection'
import { toString } from '../../helpers/common'
import { getAdAccountsData } from '../../helpers/handleAdAccountsSelection'
import { updateRouteParams } from '../../helpers/url'
import { getPartnershipColors } from '../../helpers/other'
import { getAvailablePlatformList, selectValidPlatform } from '../../helpers/handlePlatformSelection'

import { setThemeColor } from '../actions/app'
import { setSelectedAdAccounts } from '../actions/adAccounts'
import { selectedPartnershipIdSelector } from '../selectors/partnerships'
import { initialSelectedPartnership } from '../reducers/partnerships'

import {
  GET_PARTNERSHIPS,
  getPartnershipsSuccess,
  getPartnershipsFailure,
  setSelectedPartnership,
  clearSelectedPartnership,
  CREATE_PARTNERSHIP,
  createPartnershipSuccess,
  createPartnershipFailure,
  UPDATE_PARTNERSHIP,
  updatePartnershipSuccess,
  updatePartnershipFailure,
  DELETE_PARTNERSHIP,
  deletePartnershipSuccess,
  deletePartnershipFailure,
  sendOutstandingActionsNotificationSuccess,
  sendOutstandingActionsNotificationFailure,
  SEND_OUTSTANDING_ACTIONS_NOTIFICATION
} from '../actions/partnerships'

import {
  getPartnershipsService,
  createPartnershipService,
  updatePartnershipService,
  deletePartnershipService,
  sendOutstandingActionsNotificationService
} from '../services/partnerships'

import { ROUTE_PARAMS } from '../../constants/routes'

function* partnershipsWatcher() {
  yield all([
    // PLOP_APPEND_PATTERN_ANCHOR_WATCHER
    takeEvery(GET_PARTNERSHIPS, getPartnershipsWorker),
    takeEvery(CREATE_PARTNERSHIP, createPartnershipWorker),
    takeEvery(UPDATE_PARTNERSHIP, updatePartnershipWorker),
    takeEvery(DELETE_PARTNERSHIP, deletePartnershipWorker),
    takeEvery(SEND_OUTSTANDING_ACTIONS_NOTIFICATION, sendOutstandingActionsNotificationWorker)
  ])
}

// PLOP_APPEND_PATTERN_ANCHOR_WORKER

function* getPartnershipsWorker({ params }) {
  try {
    const partnerships = yield call(getPartnershipsService, params)

    yield put(getPartnershipsSuccess(partnerships))

    // set partnership data or select new partnership if current selectedPartnershipId is missed in new list
    const currentPartnershipId = yield select(selectedPartnershipIdSelector)
    const selectedPartnershipId = getInitialPartnershipId(partnerships.results, currentPartnershipId)
    const selectedPartnershipData =
      (partnerships.results &&
        partnerships.results.find(({ id }) => toString(id) === toString(selectedPartnershipId))) ||
      initialSelectedPartnership

    if (selectedPartnershipData && !!Object.keys(selectedPartnershipData).length) {
      const themePartnershipColors = getPartnershipColors(selectedPartnershipData)
      if (themePartnershipColors) {
        yield put(setThemeColor(themePartnershipColors))
      }

      yield put(setSelectedAdAccounts(getAdAccountsData(selectedPartnershipData)))
      yield put(setSelectedPartnership(selectedPartnershipData))

      // update the id in URL and fetch new data related on partnership and it's subfields(adAccounts for different platform and etc..)
      updateRouteParams({
        [ROUTE_PARAMS.partnership]: toString(selectedPartnershipData.id)
      })

      // each partnership has it's own list of available platforms
      // so after partnership load check if correct/available platform was selected
      const availablePlatformList = getAvailablePlatformList(selectedPartnershipData.providers)
      selectValidPlatform(availablePlatformList)
    }
  } catch (e) {
    yield put(getPartnershipsFailure(e))
  }
}

function* createPartnershipWorker({ partnershipData }) {
  try {
    const response = yield call(createPartnershipService, partnershipData)

    yield put(createPartnershipSuccess(response))
  } catch (e) {
    yield put(createPartnershipFailure(e))
  }
}

function* updatePartnershipWorker({ id, partnershipData }) {
  try {
    const response = yield call(updatePartnershipService, id, partnershipData)

    yield put(updatePartnershipSuccess(response))

    const selectedPartnershipId = yield select(selectedPartnershipIdSelector)

    // if selected partnership was updated, we also need to update it in redux state
    const shouldUpdateSelectedPartnership = Number(id) === Number(selectedPartnershipId)

    if (shouldUpdateSelectedPartnership) {
      yield put(setSelectedPartnership(response))

      // also update selected ad accounts, and validate selected platform
      // (the same logic happens when we change partnership)
      yield put(setSelectedAdAccounts(getAdAccountsData(response)))

      const availablePlatformList = getAvailablePlatformList(response.providers)
      selectValidPlatform(availablePlatformList)
    }
  } catch (e) {
    yield put(updatePartnershipFailure(e))
  }
}

function* deletePartnershipWorker({ params }) {
  try {
    yield call(deletePartnershipService, params)

    const selectedPartnershipId = yield select(selectedPartnershipIdSelector)

    yield put(deletePartnershipSuccess(params.id))

    // if selected partnership was deleted, we also need to clear if from redux state
    const shouldClearSelectedPartnership = params.id === Number(selectedPartnershipId)

    if (shouldClearSelectedPartnership) {
      yield put(clearSelectedPartnership())
    }
  } catch (e) {
    yield put(deletePartnershipFailure(e))
  }
}

function* sendOutstandingActionsNotificationWorker({ partnershipId }) {
  try {
    const response = yield call(sendOutstandingActionsNotificationService, partnershipId)

    yield put(sendOutstandingActionsNotificationSuccess(response))
  } catch (e) {
    yield put(sendOutstandingActionsNotificationFailure(e))
  }
}

export default partnershipsWatcher
